import styled, { css } from 'styled-components/macro'
import { respondAbove } from 'styles/mediaQueries'
import CalendarStarYellow from 'assets/images/calendar/star-calendar_yellow.svg'
import CalendarStarYellowReversed from 'assets/images/calendar/star-calendar_yellow-reversed.svg'
import { colorNames } from 'styles/theme'

interface WrapperProps {
    hasDarkMode?: boolean
    hasAdditionalInfo?: boolean
    hasDaysFromEarlierStepsMarked?: boolean
}

export const Wrapper = styled.div<WrapperProps>`
    width: 100%;
    display: flex;
    justify-content: center;
    background-color: ${({ theme, hasDarkMode }) =>
        hasDarkMode ? colorNames.darkmode_900 : theme.colors.grayscale_0};
    color: ${({ theme }) => theme.colors.secondary_500};
    padding: 0 16px;

    @media ${respondAbove.md} {
        width: 786px !important;
        max-width: 100%;
    }

    .CalendarMonth {
        padding: 0 !important;

        &_caption {
            padding-top: 17px;
            padding-bottom: 71px;

            @media ${respondAbove.md} {
                padding-top: 40px;
                padding-bottom: 86px;
                margin-left: 22px;
            }
        }

        &_table {
            border-collapse: unset;
            border-spacing: 0 2px;
            @media ${respondAbove.xs} {
                width: 100%;
            }
        }
    }

    .DayPickerNavigation .CalendarMonth_caption {
        width: 100%;
        margin-left: 10px;
        text-align: center;
    }

    .CalendarMonthGrid_month__horizontal {
        width: 100%;

        @media ${respondAbove.md} {
            width: 684px;
        }
    }

    .CalendarDay {
        cursor: pointer;
        font-size: 14px;
        line-height: 20px;

        &__highlighted_calendar {
            position: relative;
            background-color: ${({ theme }) => theme.colors.grayscale_0};

            ::after {
                position: absolute;
                width: 12px;
                height: 12px;
                bottom: 4px;
                left: calc(50% - 6px);
                content: '';
                background: ${({ hasDarkMode }) =>
                    hasDarkMode
                        ? `url(${CalendarStarYellowReversed})`
                        : `url(${CalendarStarYellow})`};
                background-position: center;
                background-repeat: no-repeat;

                @media ${respondAbove.xl} {
                    bottom: unset;
                    left: 50;
                    top: 8px;
                }
            }
        }

        &__default {
            border: 0;
            color: ${({ theme }) => theme.colors.secondary_500};
        }

        &:focus-visible:not(.CalendarDay__selected):not(.CalendarDay__selected_span):not(.CalendarDay__hovered):not(.CalendarDay__hovered_span),
        &:hover:not(.CalendarDay__selected):not(.CalendarDay__selected_span):not(.CalendarDay__hovered):not(.CalendarDay__hovered_span) {
            background-color: ${({ theme }) => theme.colors.grayscale_300};
            border-radius: 8px;
        }

        /* day out of available/active range */
        &__blocked_out_of_range {
            color: ${({ theme }) => theme.colors.grayscale_500};

            &:hover:not(.CalendarDay__selected) {
                background-color: ${({ theme }) =>
                    theme.colors.grayscale_200} !important;
            }
        }
        &__blocked_calendar {
            background-color: ${({ theme }) => theme.colors.grayscale_0};
            color: ${({ theme }) => theme.colors.grayscale_500};
        }
        &__selected {
            border-radius: 8px;
            color: ${({ theme }) => theme.colors.grayscale_0};
            background-color: ${({ theme }) => theme.colors.secondary_500};

            &_start {
                border-radius: 8px 0 0 8px;
                &.CalendarDay__selected_end {
                    border-radius: 8px;
                    &::before {
                        display: none;
                    }
                }
            }

            &_end {
                position: relative;
                border-radius: 0 8px 8px 0;
                &::before {
                    background-color: ${({ theme }) =>
                        theme.colors.secondary_500};
                    content: '';
                    z-index: 0;
                    display: block;
                    width: 2px;
                    height: 100%;
                    position: absolute;
                    left: -2px;
                }
            }

            &_span {
                :not(.CalendarDay__blocked_calendar) {
                    color: ${({ theme }) => theme.colors.grayscale_0};
                    background-color: ${({ theme }) =>
                        theme.colors.secondary_500};
                    position: relative;

                    &::before {
                        background-color: ${({ theme }) =>
                            theme.colors.secondary_500};
                        content: '';
                        z-index: 0;
                        display: block;
                        width: 2px;
                        height: 100%;
                        position: absolute;
                        left: -2px;
                    }
                }
            }
        }
    }
    .CalendarDay__hovered,
    .CalendarDay__hovered_span {
        position: relative;
        background-color: ${({ theme }) => theme.colors.grayscale_300};
        color: ${({ theme }) => theme.colors.secondary_500};

        &:hover {
            border-radius: 0 8px 8px 0;
        }

        &::before {
            background-color: ${({ theme }) => theme.colors.grayscale_0};
            content: '';
            display: block;
            width: 2px;
            height: 100%;
            position: absolute;
            left: -2px;
        }
    }

    .CalendarDay__outside {
        color: ${({ theme }) => theme.colors.grayscale_500};
    }

    .DayPickerNavigation {
        @media ${respondAbove.md} {
            min-width: 316px !important;
            width: 786px !important;
        }
    }
    .DayPicker {
        & > div:first-of-type > div:first-of-type {
            width: unset !important;
        }
    }
    .DayPicker,
    .DayPicker_focusRegion,
    .DayPicker_transitionContainer {
        width: 100% !important;
        @media ${respondAbove.md} {
            min-height: 414px !important;
            width: 786px !important;
        }
    }
    .DayPicker_weekHeaders {
        width: 100% !important;
        margin-left: 0 !important;

        @media ${respondAbove.md} {
            min-width: 316px !important;
            width: 786px !important;
        }
    }
    .DayPicker_weekHeader {
        width: 100% !important;
        padding: 0 !important;
        top: 72px;

        @media ${respondAbove.md} {
            min-width: 316px !important;
            top: 104px;
        }
        &_ul {
            width: 100% !important;

            @media ${respondAbove.md} {
                width: 706px !important;
                margin-left: 40px;
            }
        }
        &_li {
            color: ${({ theme }) => theme.colors.grayscale_900};
            font-size: 14px;
            line-height: 20px;
            width: calc(100% / 7) !important;

            @media ${respondAbove.md} {
                min-width: 45px !important;
                padding: 12px 14px 10px;
                text-align: right;
                width: 98px !important;
                height: 44px;
                font-size: 16px;
                line-height: 22px;
            }
        }
    }
    .CalendarMonthGrid {
        width: 100% !important;
        &__horizontal {
            left: 0;
        }

        @media ${respondAbove.md} {
            min-width: 316px !important;
            padding: 0 40px;
        }
    }
    .CalendarMonth_table {
        width: 100% !important;
    }

    /* stylelint-disable -- unclear and not important warning, no time right now // TODO */
    ${({ theme, hasDarkMode }) =>
        hasDarkMode &&
        css`
            .DayPicker__horizontal,
            .CalendarMonthGrid,
            .CalendarMonth,
            .CalendarDay__default {
                background-color: ${colorNames.darkmode_900};
            }
            .CalendarDay__default {
                color: ${theme.colors.grayscale_200};
            }
            .CalendarDay__blocked_out_of_range,
            .CalendarDay__outside {
                color: ${theme.colors.grayscale_700};
            }
            .CalendarDay__selected,
            .CalendarDay:hover {
                background-color: ${colorNames.darkmode_500} !important;
            }
            .CalendarDay__hovered_span,
            .CalendarDay__selected_span,
            .CalendarDay__selected_end {
                background-color: ${colorNames.darkmode_500} !important;

                &::before {
                    background-color: ${colorNames.darkmode_500} !important;
                }
            }
            .DayPicker_transitionContainer {
                border-radius: 0;
            }
            .DayPicker_weekHeader_li {
                color: ${theme.colors.grayscale_400};
            }
        `}
    ${({ hasDarkMode, hasAdditionalInfo }) =>
        hasDarkMode &&
        hasAdditionalInfo &&
        css`
            .DayPickerNavigation {
                @media ${respondAbove.xl} {
                    width: 44% !important;
                }
            }
            .CalendarMonth {
                &_caption {
                    padding-bottom: 48px;
                    min-height: 156px;

                    @media ${respondAbove.xl} {
                        padding-bottom: 86px;
                        min-height: auto;
                    }
                }
            }
            .DayPicker_weekHeader {
                margin-top: 48px;

                @media ${respondAbove.xl} {
                    margin-top: 0;
                }
            }
        `}

    /* current day */
    /* - highlighted with primary color and bolder font */
    /* - hovered: background the same as at standard day */
    /* - selected: layout the same as at standard selected day, bolder font */
    /* - ! apply primary color even when the day is out of active or available range */
    /* - ! do not apply while showing days selected in preceding steps */
    ${({ hasDaysFromEarlierStepsMarked, theme }) =>
        !hasDaysFromEarlierStepsMarked &&
        css`
            .CalendarDay__today {
                &:not(.CalendarDay__selected) {
                    color: ${theme.colors.primary_500};
                    font-family: ${theme.fonts.text_bold};

                    &:hover {
                        background-color: ${theme.colors.grayscale_300};
                    }

                    &.CalendarDay__blocked_out_of_range {
                        &:hover {
                            color: ${theme.colors.grayscale_500};
                        }
                    }
                }
                &.CalendarDay__selected {
                    font-family: ${theme.fonts.text_bold};
                }
            }
        `}
`
